import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Music2, Mail, MessageCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { trackClick } from "@/lib/track";
import { openWhatsAppHub } from "@/components/WhatsAppHub";
import { MaskUp } from "@/components/Kinetic";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export const ConnectSection = () => {
  const [s, setS] = useState({});

  useEffect(() => {
    api.get("/settings/public").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const items = [
    { key: "instagram", label: "Instagram", sub: "@roamieslife", icon: Instagram,
      href: s.instagram, action: "instagram", show: !!s.instagram },
    { key: "facebook", label: "Facebook", sub: "Roamies Group Adventures", icon: Facebook,
      href: s.facebook, action: "facebook", show: !!s.facebook },
    { key: "tiktok", label: "TikTok", sub: "@roamieslife", icon: Music2,
      href: s.tiktok, action: "tiktok", show: !!s.tiktok },
    { key: "whatsapp", label: "WhatsApp", sub: "Chat, Channel & Community", icon: MessageCircle,
      onClick: () => openWhatsAppHub(), show: true },
    { key: "email", label: "Email", sub: s.email || "", icon: Mail,
      href: s.email ? `mailto:${s.email}` : "", action: "email", show: !!s.email },
  ].filter((i) => i.show);

  return (
    <section className="bg-ink text-cream noise-bg py-16 sm:py-24 relative overflow-hidden" data-testid="connect-with-roamies-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-flame mb-4">Connect With Roamies</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
            <MaskUp>The adventure</MaskUp>
            <MaskUp delay={0.08}>doesn't end here<span className="text-flame">.</span></MaskUp>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-cream/70 leading-relaxed">
            Follow the journey. Meet the community. Be first to know where we're roaming next.
          </p>
        </motion.div>

        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {items.map((item) => {
            const cls = "text-left p-5 border border-cream/15 bg-white/5 hover:bg-flame hover:border-flame transition-colors duration-300 flex flex-col gap-6 group cursor-pointer";
            const inner = (
              <>
                <item.icon className="w-6 h-6 text-flame group-hover:text-white transition-colors" />
                <span>
                  <span className="block font-display text-xl uppercase tracking-tight">{item.label}</span>
                  <span className="block text-xs text-cream/50 group-hover:text-white/80 mt-1 truncate">{item.sub}</span>
                </span>
              </>
            );
            return item.href ? (
              <a key={item.key} href={item.href} target={item.href.startsWith("mailto:") ? undefined : "_blank"}
                rel="noopener noreferrer" onClick={() => trackClick(item.action)}
                data-testid={`connect-social-${item.key}`} className={cls}>
                {inner}
              </a>
            ) : (
              <button key={item.key} onClick={item.onClick} data-testid={`connect-social-${item.key}`} className={cls}>
                {inner}
              </button>
            );
          })}
        </motion.div>

        {s.whatsapp_community && (
          <motion.a {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}
            href={s.whatsapp_community} target="_blank" rel="noopener noreferrer"
            onClick={() => trackClick("whatsapp_community")}
            data-testid="connect-whatsapp-community-cta"
            className="mt-10 w-full sm:w-auto inline-flex bg-flame hover:bg-flame/90 text-white font-bold uppercase tracking-[0.2em] px-8 py-5 text-sm border-2 border-cream/20 shadow-[6px_6px_0_0_rgba(247,247,245,0.25)] hover:shadow-[6px_6px_0_0_#f7f7f5] transition-all duration-300 items-center justify-center gap-3 cursor-pointer">
            Join the ROAMIES WhatsApp Community <ArrowRight className="w-5 h-5" />
          </motion.a>
        )}
      </div>
    </section>
  );
};
